import React, { useCallback, useEffect, useState } from 'react';
import { PenLine, CheckCircle2, ChevronDown, ChevronUp, Stamp, FileText, ShieldCheck, Ban } from 'lucide-react';
import { api } from '../../services/api';
import SignaturePad from '../../Components/Common/SignaturePad';
import { generatePreoperacionalPdfBlob } from '../../Components/print/printPreoperacional';
import PreopDocumentLinks from './PreopDocumentLinks';
import { useUserPrefs } from '../../Components/context/UserPrefs';

const ROLE_LABELS = {
  sst: 'Coordinador SST',
  seguridad: 'Seguridad Física',
  transportes: 'Coordinador Área Transportes',
  aprobador: 'Aprobador de preoperacionales',
};
// Roles que firman (aparecen como firma en el PDF). El aprobador no firma.
const SIGN_ROLES = ['sst', 'seguridad', 'transportes'];

async function urlToDataUrl(url) {
  const res = await fetch(url);
  const blob = await res.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(String(reader.result || ''));
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

// Regenera y sube el PDF con el estado más reciente (firmas y aprobación).
// Si falla, lo ya guardado (firma o aprobación) se conserva igual.
async function regeneratePdf(item, checklistIn, documents) {
  try {
    const checklist = checklistIn || {};
    const authSignatures = SIGN_ROLES.map((key) => {
      const label = ROLE_LABELS[key];
      const a = (checklist.authorizations || []).find((x) => x.key === key) || {};
      return { role: a.role || label, name: a.name || '', signatureUrl: a.signature_url || '', notes: a.notes || '' };
    });
    const { blob } = await generatePreoperacionalPdfBlob({
      formatCode: checklist.format_code,
      formatLabel: '',
      mileageLabel: checklist.mileage_label || 'Kilometraje',
      vehicle: {
        plate: checklist.vehicle?.plate, brand: checklist.vehicle?.brand, model: checklist.vehicle?.model,
        hasDocuments: checklist.vehicle?.has_documents, soatExpiry: checklist.vehicle?.soat_expiry,
        tecnomecanicaExpiry: checklist.vehicle?.tecnomecanica_expiry, policyExpiry: checklist.vehicle?.policy_expiry,
      },
      driver: {
        nombre: checklist.driver?.nombre, cedula: checklist.driver?.cedula,
        licenseNumber: checklist.driver?.license_number, licenseExpiry: checklist.driver?.license_expiry, licenseCategory: checklist.driver?.license_category,
      },
      sections: checklist.sections || [],
      date: item.date,
      mileage: item.mileage,
      projectResponsible: checklist.project_responsible,
      hasFindings: checklist.has_findings,
      observations: checklist.observations,
      consecutivo: String(item.id).padStart(4, '0'),
      signatureUrl: checklist.signature_url,
      photoUrl: checklist.photo_url,
      evidencePhotoUrl: checklist.evidence_photo_url,
      documents: documents || [],
      approval: checklist.approval || null,
      authSignatures,
    });
    const pdfDataUrl = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(String(reader.result || ''));
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
    await api.preoperationals.uploadPdf({ preop_id: item.id, pdf_base64: pdfDataUrl });
  } catch (_) {
    // silencioso a propósito
  }
}

function SignRow({ item, role, onSigned }) {
  const { currentUser } = useUserPrefs();
  const [dataUrl, setDataUrl] = useState('');
  const [signing, setSigning] = useState(false);
  const [error, setError] = useState('');
  const [savedSignatureUrl, setSavedSignatureUrl] = useState('');
  const [usingSaved, setUsingSaved] = useState(false);
  const [loadingSaved, setLoadingSaved] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const [notes, setNotes] = useState('');

  // Firma guardada en el perfil del usuario (people.firma_digital): si
  // existe, se carga y se deja lista automáticamente (el usuario solo tiene
  // que dar "Firmar y autorizar"); si no existe, solo queda disponible
  // dibujarla a mano con el SignaturePad de abajo.
  const [savedSignatureDebug, setSavedSignatureDebug] = useState('');

  useEffect(() => {
    let alive = true;
    const cedula = String(currentUser?.responsibleCedula || currentUser?.responsible_cedula || '').trim();
    if (!cedula) {
      setSavedSignatureDebug('Tu usuario no tiene cédula asociada en el perfil (responsibleCedula vacío), así que no se puede buscar tu firma guardada.');
      return undefined;
    }
    (async () => {
      setLoadingSaved(true);
      try {
        const res = await api.people.get({ dni: cedula, clean: 1 });
        const person = Array.isArray(res?.data) ? res.data[0] : res?.data;
        if (!alive) return;
        if (!person) {
          setSavedSignatureDebug(`No se encontró a nadie en Recursos Humanos con la cédula ${cedula}.`);
        } else if (!person.signature) {
          setSavedSignatureDebug(`Encontré tu perfil (${person.name || cedula}) pero no tiene firma cargada.`);
        } else {
          setSavedSignatureUrl(person.signature);
          try {
            const du = await urlToDataUrl(person.signature);
            if (alive) { setDataUrl(du); setUsingSaved(true); }
          } catch (e) {
            if (alive) {
              setError('No fue posible cargar tu firma guardada.');
              setSavedSignatureDebug(`La firma existe (${person.signature}) pero no se pudo descargar: ${e?.message || e}`);
            }
          }
        }
      } catch (e) {
        if (alive) setSavedSignatureDebug(`Error consultando tu perfil: ${e?.message || e}`);
      }
      if (alive) setLoadingSaved(false);
    })();
    return () => { alive = false; };
  }, [currentUser?.responsibleCedula, currentUser?.responsible_cedula]);

  const sign = async () => {
    if (!dataUrl) { setError('Dibuja tu firma antes de continuar.'); return; }
    setSigning(true);
    setError('');
    try {
      const res = await api.preoperationals.signAuthorization({ preop_id: item.id, role, signature_base64: dataUrl, notes: notes.trim() });
      if (!res?.ok) throw new Error(res?.error || 'No fue posible firmar.');

      // Regenerar el PDF con la firma recién agregada, para que el
      // documento final siempre refleje el estado más reciente.
      await regeneratePdf(item, res.checklist, res.documents);

      onSigned(item.id, role);
    } catch (e) {
      setError(e?.message || 'No fue posible firmar.');
    } finally {
      setSigning(false);
    }
  };

  return (
    <div className="mt-3 border-t pt-3">
      <div className="text-xs text-gray-600 mb-1">Tu firma como {ROLE_LABELS[role] || role}</div>
      {loadingSaved ? (
        <div className="text-xs text-gray-500 flex items-center gap-1.5 py-2">
          <Stamp size={14} /> Comprobando firma guardada...
        </div>
      ) : savedSignatureUrl && usingSaved ? (
        <div className="border rounded-lg bg-white p-2">
          <img src={dataUrl || savedSignatureUrl} alt="Tu firma guardada" className="h-[100px] w-full object-contain" />
          <button type="button" onClick={() => { setUsingSaved(false); setDataUrl(''); }} className="text-xs text-blue-600 mt-1">
            Dibujar otra firma
          </button>
        </div>
      ) : (
        <>
          <SignaturePad height={100} onChange={setDataUrl} />
          {savedSignatureDebug && <div className="text-[11px] text-gray-400 mt-1">{savedSignatureDebug}</div>}
        </>
      )}
      {showNotes ? (
        <div className="mt-2">
          <label className="text-xs text-gray-600 mb-1 block">Observación (si encontraste algún problema con el vehículo)</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            className="w-full border rounded-lg px-2 py-1.5 text-xs"
            placeholder="Ej: llanta delantera desgastada, revisar antes de despachar..."
          />
        </div>
      ) : (
        <button type="button" onClick={() => setShowNotes(true)} className="mt-2 text-xs text-blue-600 hover:text-blue-700">
          + Agregar observación
        </button>
      )}
      {error && <div className="text-xs text-red-600 mt-1">{error}</div>}
      <button
        type="button"
        onClick={sign}
        disabled={signing}
        className="mt-2 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-white text-xs font-semibold disabled:opacity-60"
        style={{ backgroundColor: '#16A34A' }}
      >
        <PenLine size={14} /> {signing ? 'Firmando...' : 'Firmar y autorizar'}
      </button>
    </div>
  );
}

// Aprobador de preoperacionales (permiso "Aprueba preoperacionales"): no
// firma. Aprueba (el registro pasa a Seguridad Física) o bloquea la salida
// (Portería lo verá bloqueado). Puede dejar una observación en ambos casos;
// al bloquear es obligatoria.
function ApproveRow({ item, onDone }) {
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [confirmBlock, setConfirmBlock] = useState(false);

  const decide = async (decision) => {
    setError('');
    if (decision === 'bloqueado' && !notes.trim()) {
      setError('Escribe el motivo para bloquear la salida.');
      return;
    }
    setBusy(decision);
    try {
      const res = await api.preoperationals.approve({ preop_id: item.id, decision, notes: notes.trim() });
      if (!res?.ok) throw new Error(res?.error || 'No fue posible guardar la decisión.');
      await regeneratePdf(item, res.checklist, res.documents);
      onDone(item.id, 'aprobador');
    } catch (e) {
      setError(e?.message || 'No fue posible guardar la decisión.');
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="mt-3 border-t pt-3">
      <div className="text-xs text-gray-600 mb-1">Tu revisión como {ROLE_LABELS.aprobador} (no requiere firma)</div>
      <label className="text-xs text-gray-600 mb-1 block">Observación (opcional para aprobar, obligatoria para bloquear)</label>
      <textarea
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        rows={2}
        className="w-full border rounded-lg px-2 py-1.5 text-xs"
        placeholder="Ej: falta la orden de trabajo, el conductor no presentó aptitud física..."
      />
      {error && <div className="text-xs text-red-600 mt-1">{error}</div>}
      {confirmBlock ? (
        <div className="mt-2 rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-800">
          ¿Bloquear la salida de {item.plate}? El vehículo quedará bloqueado en Portería y no pasará a Seguridad Física.
          <div className="mt-2 flex gap-2">
            <button type="button" onClick={() => decide('bloqueado')} disabled={!!busy}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-white text-xs font-semibold bg-red-600 hover:bg-red-700 disabled:opacity-60">
              <Ban size={14} /> {busy === 'bloqueado' ? 'Bloqueando...' : 'Sí, bloquear salida'}
            </button>
            <button type="button" onClick={() => setConfirmBlock(false)} disabled={!!busy}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-gray-300 bg-white text-gray-700">
              Cancelar
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-2 flex flex-wrap gap-2">
          <button type="button" onClick={() => decide('aprobado')} disabled={!!busy}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-white text-xs font-semibold disabled:opacity-60"
            style={{ backgroundColor: '#16A34A' }}>
            <ShieldCheck size={14} /> {busy === 'aprobado' ? 'Aprobando...' : 'Aprobar'}
          </button>
          <button type="button" onClick={() => { setError(''); if (!notes.trim()) { setError('Escribe el motivo para bloquear la salida.'); return; } setConfirmBlock(true); }} disabled={!!busy}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border border-red-300 bg-white text-red-700 hover:bg-red-50 disabled:opacity-60">
            <Ban size={14} /> Bloquear salida
          </button>
        </div>
      )}
    </div>
  );
}

const CITY_OPTIONS = ['Popayán', 'Cali', 'Santander de Quilichao'];

const SEDE_CITY = { popayan: 'Popayán', emcali: 'Cali', 'santander-de-quilichao': 'Santander de Quilichao' };
// Solo lo vigente: lo que lleva más de 7 días sin firma se considera vencido.
const MAX_DAYS = 7;

// embedded/onInfo: igual que SeguridadPendingExits (pestaña en Portería).
export default function PreopPendingAuthorizations({ location, embedded = false, onInfo }) {
  const [roles, setRoles] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  // Arranca en la ciudad de la sede de la URL: antes arrancaba en "Todas" y
  // en /sede/popayan se veían pendientes de Cali y Santander.
  const [cityFilter, setCityFilter] = useState(SEDE_CITY[String(location || '').toLowerCase()] || '');
  useEffect(() => { setCityFilter(SEDE_CITY[String(location || '').toLowerCase()] || ''); }, [location]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.preoperationals.pendingAuthorization({ city: cityFilter, max_days: MAX_DAYS });
      if (res?.ok) {
        setRoles(Array.isArray(res.roles) ? res.roles : []);
        setItems(Array.isArray(res.data) ? res.data : []);
      }
    } catch (_) {
      // Silencioso: si el usuario no tiene rol de autorización, simplemente no se muestra nada.
    } finally {
      setLoading(false);
    }
  }, [cityFilter]);

  useEffect(() => { load(); }, [load]);

  const handleSigned = (id, role) => {
    setItems((prev) => prev
      .map((it) => (it.id === id ? { ...it, pending_roles: it.pending_roles.filter((r) => r !== role) } : it))
      .filter((it) => it.pending_roles.length > 0));
    setOpenId(null);
  };

  // Mientras no se sepa (primera carga) o el usuario no tenga rol de
  // autorización, no se muestra nada. Una vez se sabe que sí tiene rol, el
  // widget (con el filtro de ciudad) se queda visible aunque el filtro
  // elegido no tenga pendientes en ese momento — para poder cambiar de
  // ciudad sin que la caja entera desaparezca.
  useEffect(() => { if (onInfo) onInfo({ visible: roles.length > 0, count: items.length }); }, [onInfo, roles.length, items.length]);

  if (roles.length === 0) return null;
  const onlyApprover = roles.length === 1 && roles[0] === 'aprobador';

  return (
    <div className={embedded ? '' : 'bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4'}>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
        <div className="flex items-center gap-2">
          <PenLine size={18} className="text-amber-700" />
          <h3 className="text-sm font-semibold text-amber-900">{onlyApprover ? 'Pendientes de tu aprobación' : roles.includes('aprobador') ? 'Pendientes de tu firma o aprobación' : 'Pendientes de tu firma'} ({items.length})</h3>
          {loading && <span className="text-xs text-amber-600">actualizando…</span>}
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {['', ...CITY_OPTIONS].map((c) => (
            <button
              key={c || 'todas'}
              type="button"
              onClick={() => setCityFilter(c)}
              className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${cityFilter === c ? 'bg-amber-600 text-white border-amber-600' : 'bg-white text-amber-800 border-amber-300 hover:bg-amber-100'}`}
            >
              {c || 'Todas las sedes'}
            </button>
          ))}
        </div>
      </div>
      <p className="text-xs text-amber-700 mb-3">
        Preoperacionales de los últimos {MAX_DAYS} días que necesitan tu autorización como {roles.map((r) => ROLE_LABELS[r] || r).join(' / ')}.
      </p>
      {items.length === 0 && (
        <div className="text-xs text-amber-700 bg-white border border-amber-200 rounded-lg px-3 py-2">
          {cityFilter ? `No hay pendientes en ${cityFilter} en este momento.` : 'No tienes preoperacionales pendientes de firma en este momento.'}
        </div>
      )}
      <div className="space-y-2">
        {items.map((item) => {
          const open = openId === item.id;
          return (
            <div key={item.id} className="bg-white border border-amber-200 rounded-lg p-3">
              <button type="button" onClick={() => setOpenId(open ? null : item.id)} className="w-full flex items-center justify-between text-left">
                <div>
                  <div className="flex flex-wrap items-center gap-2 text-sm font-semibold text-gray-900">
                    {item.plate} · {formatDate(item.date)}
                    <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-900">Te toca: {item.pending_roles.map((r) => ROLE_LABELS[r] || r).join(', ')}</span>
                  </div>
                  <div className="text-xs text-gray-500">
                    {item.driver_name || 'Sin conductor'} · Estado: {item.status}
                    {Array.isArray(item.cities) && item.cities.length > 0 ? ` · ${item.cities.join(' / ')}` : ''}
                  </div>
                </div>
                {open ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
              </button>
              {open && (
                <div>
                  {item.pdf_url ? (
                    <a
                      href={item.pdf_url}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-2 flex items-center gap-1.5 text-xs font-semibold text-blue-700 hover:text-blue-800"
                    >
                      <FileText size={14} /> Ver PDF del preoperacional antes de firmar
                    </a>
                  ) : (
                    <div className="mt-2 text-xs text-gray-400">El PDF de este preoperacional aún no está disponible.</div>
                  )}
                  {/* Adjuntos opcionales que sube el conductor desde la APK */}
                  <PreopDocumentLinks documents={item.documents} variant="panel" />
                  {item.observations && (
                    <div className="mt-2 text-xs text-gray-600 bg-gray-50 rounded p-2">{item.observations}</div>
                  )}
                  {item.approval?.status === 'aprobado' && (
                    <div className="mt-2 text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded p-2">
                      <b>Aprobado</b> por {item.approval.by || '—'}{item.approval.at ? ` · ${String(item.approval.at).slice(0, 16)}` : ''}
                      {item.approval.notes ? <div className="mt-0.5">Observación: {item.approval.notes}</div> : null}
                    </div>
                  )}
                  {item.pending_roles.map((role) => (role === 'aprobador'
                    ? <ApproveRow key={role} item={item} onDone={handleSigned} />
                    : <SignRow key={role} item={item} role={role} onSigned={handleSigned} />
                  ))}
                </div>
              )}
              {!open && (
                <div className="mt-1 flex items-center gap-1 text-xs text-gray-400">
                  <CheckCircle2 size={12} /> {item.pending_roles.every((r) => r === 'aprobador') ? 'Toca para revisar y aprobar' : 'Toca para revisar y firmar'}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function formatDate(value) {
  const raw = String(value || '').trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  return `${raw.slice(8, 10)}/${raw.slice(5, 7)}/${raw.slice(0, 4)}`;
}
